import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Tag } from 'lucide-react';
import { MarketingProgramResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import MarketingProgramForm from '../components/MarketingProgramForm';
import { useAuth } from '../contexts/AuthContext';

const MarketingPrograms: React.FC = () => {
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [programs, setPrograms] = useState<MarketingProgramResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    type: '' as '' | 'coupon' | 'no-coupon',
    isActive: '' as '' | 'true' | 'false',
    startDate: '',
    endDate: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState<MarketingProgramResource | null>(null);

  // Load programs
  const loadPrograms = async () => {
    try {
      setLoading(true);
      const response = await api.getMarketingPrograms(page, perPage, search, {
        type: filters.type || undefined,
        isActive: filters.isActive ? filters.isActive === 'true' : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setPrograms(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading marketing programs:', error);
      // Mock data for development
      setPrograms([
        {
          id: '1',
          name: 'New Year Special',
          description: 'Special discount for new year celebration',
          type: 'no-coupon',
          programCode: 'NEWYEAR2024',
          minimumMonth: 3,
          discountType: 'percent',
          discountValue: 20,
          isActive: true,
          usageLimit: 1000,
          usageLimitPerUser: 1,
          startsAt: '2024-01-01',
          endsAt: '2024-01-31',
          createdAt: '2024-01-01T00:00:00.000Z',
          updatedAt: '2024-01-01T00:00:00.000Z',
        },
        {
          id: '2',
          name: 'Loyalty Coupon',
          description: 'Coupon for loyal customers',
          type: 'coupon',
          couponPrefix: 'LOYAL',
          minimumMonth: 6,
          discountType: 'fixed',
          discountValue: 50000,
          isActive: true,
          usageLimit: 500,
          usageLimitPerUser: 2,
          expiredDay: 30,
          createdAt: '2024-01-01T00:00:00.000Z',
          updatedAt: '2024-01-01T00:00:00.000Z',
        },
      ]);
      setTotal(2);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load programs when dependencies change
  useEffect(() => {
    loadPrograms();
  }, [page, perPage, search, filters]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleFiltersClear = () => {
    setFilters({
      type: '',
      isActive: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const handleCreate = () => {
    setEditingProgram(null);
    setShowForm(true);
  };

  const handleViewDetail = (program: MarketingProgramResource) => {
    navigate(`/marketing-programs/${program.id}`, { state: { program } });
  };

  const handleEdit = (program: MarketingProgramResource) => {
    setEditingProgram(program);
    setShowForm(true);
  };

  const handleDelete = async (programId: string) => {
    if (window.confirm('Are you sure you want to delete this marketing program?')) {
      try {
        await api.deleteMarketingProgram(programId);
        loadPrograms();
      } catch (error) {
        console.error('Error deleting marketing program:', error);
        alert('Failed to delete marketing program');
      }
    }
  };

  const handleFormSubmit = async () => {
    setShowForm(false);
    setEditingProgram(null);
    loadPrograms();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'coupon':
        return 'bg-blue-100 text-blue-800';
      case 'no-coupon':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'coupon':
        return 'Coupon';
      case 'no-coupon':
        return 'No Coupon';
      default:
        return type;
    }
  };

  const getDiscountDisplay = (type: string, value: number) => {
    if (type === 'percent') {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Programs</h1>
          <p className="text-gray-600">Manage marketing programs and promotions</p>
        </div>
        {canManage && (
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Program
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filters.type}
              onChange={(e) => handleFiltersChange({ ...filters, type: e.target.value as '' | 'coupon' | 'no-coupon' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              <option value="coupon">Coupon</option>
              <option value="no-coupon">No Coupon</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.isActive}
              onChange={(e) => handleFiltersChange({ ...filters, isActive: e.target.value as '' | 'true' | 'false' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="flex space-x-2">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFiltersChange({ ...filters, startDate: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFiltersChange({ ...filters, endDate: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="End Date"
            />
          </div>
        </div>

        {/* Active Filters */}
        {(filters.type || filters.isActive || filters.startDate || filters.endDate) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filters.type && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Type: {getTypeLabel(filters.type)}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, type: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.isActive && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Status: {filters.isActive === 'true' ? 'Active' : 'Inactive'}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, isActive: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.startDate && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  From: {formatDate(filters.startDate)}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, startDate: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.endDate && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  To: {formatDate(filters.endDate)}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, endDate: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={handleFiltersClear}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Programs List */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading marketing programs..." />
        </div>
      ) : programs.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="text-gray-500">
            <Tag className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No marketing programs found</h3>
            <p className="text-gray-500 mb-4">
              {search || Object.values(filters).some(f => f) 
                ? 'Try adjusting your search or filters' 
                : 'Get started by creating your first marketing program'
              }
            </p>
            {canManage && !search && !Object.values(filters).some(f => f) && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Program
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Program
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Period
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {programs.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        {program.image && (
                          <div className="flex-shrink-0 h-10 w-10 mr-3">
                            <img
                              src={program.image}
                              alt={program.name}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <button
                            onClick={() => handleViewDetail(program)}
                            className="text-sm font-medium text-primary-600 hover:text-primary-900 break-words text-left"
                          >
                            {program.name}
                          </button>
                          {program.description && (
                            <div className="text-sm text-gray-500 break-words mt-1">
                              {program.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            Min. {program.minimumMonth} month{program.minimumMonth > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(program.type)}`}>
                        {getTypeLabel(program.type)}
                      </span>
                      {program.type === 'no-coupon' && program.programCode && (
                        <div className="text-xs text-gray-500 mt-1">
                          Code: {program.programCode}
                        </div>
                      )}
                      {program.type === 'coupon' && program.couponPrefix && (
                        <div className="text-xs text-gray-500 mt-1">
                          Prefix: {program.couponPrefix}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {getDiscountDisplay(program.discountType, program.discountValue)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {program.discountType === 'percent' ? 'Percentage' : 'Fixed Amount'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {program.usageLimit ? `${program.usageLimit} total` : 'Unlimited'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {program.usageLimitPerUser ? `${program.usageLimitPerUser} per user` : 'Unlimited per user'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(program.isActive)}`}>
                        {program.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {program.type === 'no-coupon' ? (
                        <div className="text-sm text-gray-900">
                          {program.startsAt && formatDate(program.startsAt)}
                          {program.startsAt && program.endsAt && <span className="mx-1">-</span>}
                          {program.endsAt && formatDate(program.endsAt)}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-900">
                          {program.expiredDay ? `${program.expiredDay} days` : 'No expiry'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      {canManage && (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(program)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Edit program"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(program.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete program"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {programs.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} results
          </div>
          <Pagination
            currentPage={page}
            total={total}
            perPage={perPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <MarketingProgramForm
          program={editingProgram}
          onClose={() => {
            setShowForm(false);
            setEditingProgram(null);
          }}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default MarketingPrograms;
